"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { toggleSaveJob } from "@/app/jobs/saved-actions";
import { capture } from "@/lib/analytics/events";
import { MicroSurvey, shouldAskMicroSurvey } from "@/components/MicroSurvey";
import { isLocallySaved, toggleLocalSave } from "@/lib/localSavedJobs";
import type { SaveJobInput } from "@/lib/saved-jobs";

/**
 * Bookmark toggle on a job row / job page. Optimistic. Signed-out visitors
 * bookmark to this device via localStorage — no account required, the same
 * "don't gate the first useful action" move as the alert nudge (CM-169). Sits
 * above the row's stretched title link via z-10 so the click lands here.
 */
export function SaveJobButton({
  job,
  initialSaved,
  isAuthed,
  compact = true,
  block = false,
}: {
  job: SaveJobInput;
  initialSaved: boolean;
  isAuthed: boolean;
  compact?: boolean;
  /** Full-width, apply-button-height variant — a co-primary action on the job page. */
  block?: boolean;
}) {
  const [saved, setSaved] = useState(initialSaved);
  const [pending, setPending] = useState(false);
  const [askSurvey, setAskSurvey] = useState(false);
  // Starts false to match the signed-out server render exactly; a mount effect
  // (below) reconciles it from localStorage, which runs after hydration and so
  // is a normal re-render rather than a hydration mismatch.
  const [localSaved, setLocalSaved] = useState(false);
  const iconSize = block ? 14 : 12;

  useEffect(() => {
    if (!isAuthed) setLocalSaved(isLocallySaved(job.jobId));
  }, [isAuthed, job.jobId]);

  // On pages where isAuthed/initialSaved are reconciled client-side after
  // mount (job page: starts signed-out-safe, flips once the session check
  // resolves), the initial useState value below is stale by then — sync it.
  useEffect(() => {
    setSaved(initialSaved);
  }, [initialSaved]);

  const base = block
    ? "relative z-10 flex h-11 w-full items-center justify-center gap-1.5 rounded-md border text-sm font-semibold transition-colors"
    : "relative z-10 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition-colors";

  if (!isAuthed) {
    const toggleLocal = (e: React.MouseEvent) => {
      e.preventDefault();
      const nowSaved = toggleLocalSave(job);
      setLocalSaved(nowSaved);
      if (nowSaved) {
        capture("job_saved_local", { job_id: job.jobId });
        // Same "first save on this device" moment as the signed-in path below.
        if (shouldAskMicroSurvey()) setAskSurvey(true);
      }
    };
    return (
      <>
        <Link
          href={`/auth/login?next=${encodeURIComponent(`/jobs/${job.jobId}`)}`}
          onClick={toggleLocal}
          title={
            localSaved
              ? "Saved on this device — sign in to sync it to your pipeline"
              : "Save this job to this device — no account needed"
          }
          className={`${base} ${
            localSaved
              ? "border-brand-300 bg-brand-50 text-brand-700 hover:border-brand-400"
              : block
                ? "border-neutral-300 bg-white text-neutral-700 hover:border-brand-300 hover:text-brand-700"
                : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-400 hover:text-ink"
          }`}
        >
          <BookmarkIcon filled={localSaved} size={iconSize} />
          {compact && !block ? null : <span>{localSaved ? "Saved" : "Save this job"}</span>}
        </Link>
        {askSurvey ? (
          <MicroSurvey
            surveyId="first_save_intent"
            question="What were you hoping to find today?"
            placeholder="e.g. remote M&E roles at UN agencies, P-3 and up"
            onClose={() => setAskSurvey(false)}
          />
        ) : null}
      </>
    );
  }

  const toggle = async () => {
    if (pending) return;
    const prev = saved;
    setSaved(!prev);
    setPending(true);
    const res = await toggleSaveJob({ ...job, saved: prev });
    if (!res.ok) setSaved(prev);
    else setSaved(res.saved);
    setPending(false);
    // First save on this device = the first moment we've earned a question.
    if (res.ok && res.saved && shouldAskMicroSurvey()) setAskSurvey(true);
  };

  return (
    <>
    <button
      type="button"
      onClick={toggle}
      aria-pressed={saved}
      title={saved ? "Saved — click to remove" : "Save this job"}
      className={`${base} ${
        saved
          ? "border-brand-300 bg-brand-50 text-brand-700 hover:border-brand-400"
          : block
            ? "border-neutral-300 bg-white text-neutral-700 hover:border-brand-300 hover:text-brand-700"
            : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-400 hover:text-ink"
      } disabled:opacity-60`}
    >
      <BookmarkIcon filled={saved} size={iconSize} />
      <span>{saved ? "Saved" : block ? "Save this job" : "Save"}</span>
    </button>
    {askSurvey ? (
      <MicroSurvey
        surveyId="first_save_intent"
        question="What were you hoping to find today?"
        placeholder="e.g. remote M&E roles at UN agencies, P-3 and up"
        onClose={() => setAskSurvey(false)}
      />
    ) : null}
    </>
  );
}

function BookmarkIcon({ filled, size = 12 }: { filled: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden fill={filled ? "currentColor" : "none"}>
      <path
        d="M4 2.5h8a.5.5 0 0 1 .5.5v10.5l-4.5-2.6L3.5 13.5V3a.5.5 0 0 1 .5-.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
